import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Sparkle, PencilSimpleLine, MapPin } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import PaywallGate from "../components/PaywallGate";
import SEO from "../components/SEO";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";
import api from "../lib/api";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const inputCls = "mt-1 block w-full min-w-0 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] outline-none bg-white px-3 py-2.5 text-[15px]";
const ORD = (n) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
const dms = (d) => `${Math.floor(d)}°${String(Math.round((d % 1) * 60)).padStart(2, "0")}′`;

const LordCard = ({ d }) => {
  const p = d.lord_placement;
  return (
    <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="cr-lord-card">
      <div className="label-eyebrow text-muted">Your career house</div>
      <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]" data-testid="cr-tenth-lord">10th lord: {d.tenth_lord}</h2>
      <div className="grid sm:grid-cols-3 gap-3 mt-5 text-sm">
        <div className="rounded-xl border border-amber-200/70 p-3"><div className="text-xs text-muted">Ascendant · 10th sign</div><div className="font-semibold text-[#3F2D1B] mt-0.5" data-testid="cr-tenth-sign">{d.lagna} · {d.tenth_sign}</div></div>
        <div className="rounded-xl border border-amber-200/70 p-3"><div className="text-xs text-muted">{d.tenth_lord} is placed in</div><div className="font-semibold text-[#3F2D1B] mt-0.5" data-testid="cr-lord-sign">{p.rashi} {dms(p.degrees_in_rashi)}{p.house ? ` · ${ORD(p.house)} house` : ""}{p.retrograde ? " (R)" : ""}</div></div>
        <div className="rounded-xl border border-[#E8A628] p-3"><div className="text-xs text-muted">Nakshatra</div><div className="font-semibold text-[#3F2D1B] mt-0.5" data-testid="cr-lord-nakshatra">{p.nakshatra} · pada {p.pada}</div></div>
      </div>
    </section>
  );
};

const RemedyCard = ({ r }) => (
  <section className="rounded-2xl bg-white border-2 border-[#E8A628] p-5 sm:p-7" data-testid="cr-remedy-card">
    <div className="label-eyebrow text-[#B7791F] flex items-center gap-2"><PencilSimpleLine size={14} weight="duotone" /> Your career remedy</div>
    <h2 className="font-serif-display text-2xl sm:text-3xl mt-2 text-[#3F2D1B]" data-testid="cr-remedy-headline">{r.headline}</h2>
    <figure className="mt-5 max-w-md">
      <img src={r.image} alt={r.animal_label} className="w-full aspect-[4/3] object-cover rounded-xl border border-amber-200/70" data-testid="cr-remedy-image" />
      <figcaption className="text-xs text-muted mt-1.5">Reference: {r.animal_label}. Draw it in {r.colour} on white paper.</figcaption>
    </figure>
    <div className="flex flex-wrap items-center gap-4 mt-5">
      <div className="flex items-center gap-2" data-testid="cr-remedy-colour">
        {r.colour_hex.map((h) => <span key={h} className="w-9 h-9 rounded-full border-2 border-slate-300 shadow-inner" style={{ background: h }} />)}
        <span className="text-sm text-[#3F2D1B]">Colour: <b className="capitalize">{r.colour}</b></span>
      </div>
      <div className="text-sm text-[#3F2D1B]" data-testid="cr-remedy-animal">Animal: <b className="capitalize">{r.animal_label}</b></div>
    </div>
    <p className="text-[15px] leading-relaxed text-[#3F2D1B]/90 mt-5" data-testid="cr-remedy-why">{r.why}</p>
    <ol className="mt-5 space-y-2.5" data-testid="cr-remedy-steps">
      {r.steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-[15px] text-[#3F2D1B]"><span className="w-6 h-6 shrink-0 rounded-full bg-[#E8A628] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>{s}</li>
      ))}
    </ol>
    <p className="mt-5 text-sm text-muted flex items-center gap-1.5"><MapPin size={14} /> Office desk · office bag · purse</p>
  </section>
);

const Fact = ({ k, v, id }) => (
  <div className="rounded-xl border border-amber-200/70 px-3 py-2.5">
    <div className="text-[11px] uppercase tracking-wider text-muted">{k}</div>
    <div className="text-sm font-semibold text-[#3F2D1B] mt-0.5" data-testid={`cr-nak-${id}`}>{v}</div>
  </div>
);

const NakshatraDetails = ({ n, lord }) => {
  const [list, note] = n.career.split(" — ");
  const facts = [
    ["Ruling planet", n.ruling_planet, "ruler"], ["Deity", n.deity, "deity"], ["Symbol", n.symbol, "symbol"],
    ["Yoni (animal)", `${n.yoni} · ${n.yoni_gender}`, "yoni"], ["Bird", n.bird, "bird"], ["Sacred tree", n.tree, "tree"],
    ["Gana", n.gana, "gana"], ["Nadi", n.nadi, "nadi"], ["Varna", n.varna, "varna"], ["Element", n.element, "element"],
    ["Direction", n.direction, "direction"], ["Body part", n.body_part, "body"], ["Sign(s)", n.signs_covered, "signs"],
    [`Pada ${n.pada}`, `${n.pada_navamsa} navamsa · lord ${n.pada_lord}`, "pada"], ["Name syllables", n.syllables.join(" · "), "syllables"],
  ];
  return (
    <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="cr-nakshatra-card">
      <div className="label-eyebrow text-muted">Your 10th lord {lord}'s nakshatra in full</div>
      <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]">{n.name} <span className="text-lg text-muted">· nakshatra #{n.index}</span></h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 mt-5">{facts.map(([k, v, id]) => <Fact key={id} k={k} v={v} id={id} />)}</div>
      <h3 className="font-serif-display text-xl text-[#3F2D1B] mt-7">Mantras</h3>
      <div className="mt-2 space-y-2" data-testid="cr-nak-mantras">
        {n.mantras.map((m) => (
          <div key={m.label} className="rounded-xl bg-white border border-[#E8A628]/70 px-4 py-3">
            <div className="text-xs text-muted">{m.label}</div>
            <div className="font-serif-display text-xl text-[#3F2D1B]">{m.text}</div>
          </div>
        ))}
        <p className="text-sm text-[#3F2D1B]/85">{n.spiritual}</p>
      </div>
      <h3 className="font-serif-display text-xl text-[#3F2D1B] mt-7">Best professions for {n.name}</h3>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="cr-nak-professions">
        {list.replace(/\.$/, "").split(", ").map((c) => <span key={c} className="rounded-full border border-[#E8A628]/70 px-3 py-1 text-sm text-[#3F2D1B] first-letter:uppercase">{c}</span>)}
      </div>
      {note && <p className="text-sm text-muted mt-2">{note.charAt(0).toUpperCase() + note.slice(1)}</p>}
      <div className="grid sm:grid-cols-2 gap-4 mt-7">
        <div data-testid="cr-nak-strengths"><h4 className="font-semibold text-[#3F2D1B]">Strengths at work</h4>
          <ul className="mt-2 space-y-1 text-sm text-[#3F2D1B]/90 list-disc pl-5">{n.strengths.map((s) => <li key={s}>{s}</li>)}</ul></div>
        <div data-testid="cr-nak-weaknesses"><h4 className="font-semibold text-[#3F2D1B]">Watch out for</h4>
          <ul className="mt-2 space-y-1 text-sm text-[#3F2D1B]/90 list-disc pl-5">{n.weaknesses.map((s) => <li key={s}>{s}</li>)}</ul></div>
      </div>
    </section>
  );
};

const LordCareers = ({ c, lord, sign }) => (
  <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="cr-lord-careers">
    <div className="label-eyebrow text-muted">Best careers for your 10th lord</div>
    <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]">{lord} rules your career</h2>
    <p className="text-[15px] text-[#3F2D1B]/90 mt-3">With {lord} as lord of your 10th house ({sign}), you do best in work built on {c.theme}.</p>
    <div className="flex flex-wrap gap-2 mt-4" data-testid="cr-lord-career-list">
      {c.careers.map((x) => <span key={x} className="rounded-full border border-[#E8A628]/70 px-3 py-1 text-sm text-[#3F2D1B]">{x}</span>)}
    </div>
  </section>
);

const PlanetPicker = ({ planets, initial }) => {
  const [sel, setSel] = useState(initial);
  const x = planets.find((p) => p.planet === sel) || planets[0];
  return (
    <section className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="cr-planet-picker">
      <div className="label-eyebrow text-muted">Activate any house · choose a planet</div>
      <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]">Same remedy, any planet</h2>
      <label className="block mt-4 max-w-xs text-xs font-semibold text-[#3F2D1B]/70" htmlFor="cr-pick-select">Choose a planet
        <select id="cr-pick-select" value={sel} onChange={(e) => setSel(e.target.value)}
          className="mt-1 block w-full rounded-xl border-2 border-[#E8A628]/60 focus:border-[#E8A628] outline-none bg-white px-3 py-2.5 text-[15px] text-[#3F2D1B]"
          data-testid="cr-pick-select">
          {planets.map((p) => (
            <option key={p.planet} value={p.planet}>{p.planet}{p.planet === initial ? " (10th lord)" : ""}</option>
          ))}
        </select>
      </label>
      <div className="mt-6 grid sm:grid-cols-[minmax(0,260px)_1fr] gap-5 items-start" data-testid="cr-pick-result">
        <img src={x.image} alt={x.animal_label} className="w-full aspect-[4/3] object-cover rounded-xl border border-amber-200/70" data-testid="cr-pick-image" />
        <div>
          <h3 className="font-serif-display text-2xl text-[#3F2D1B]" data-testid="cr-pick-headline">{x.headline}</h3>
          <p className="mt-2 text-[15px] font-semibold text-[#B7791F]" data-testid="cr-pick-activates">{x.activates}</p>
          <div className="flex items-center gap-2 mt-3">
            {x.colour_hex.map((h) => <span key={h} className="w-7 h-7 rounded-full border-2 border-slate-300" style={{ background: h }} />)}
            <span className="text-sm text-[#3F2D1B]">Colour: <b>{x.colour}</b> · {x.rashi} {dms(x.degrees_in_rashi)} · {x.nakshatra} pada {x.pada}</span>
          </div>
          <p className="text-sm text-[#3F2D1B]/85 mt-3" data-testid="cr-pick-why">{x.why}</p>
          <ol className="mt-3 space-y-1.5 text-sm text-[#3F2D1B] list-decimal pl-5">{x.steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </div>
      </div>
    </section>
  );
};

export default function CareerRemedyPage() {
  const [form, setForm] = useState({ date: "", hour: "", minute: "", place: "", timezone: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const resultRef = useRef(null);

  useBirthDetailsPrefill((bd) => {
    const [hh, mm] = String(bd?.time || "").split(":");
    setForm((f) => ({ date: f.date || bd?.date || "", hour: f.hour || hh || "", minute: f.minute || mm || "",
      place: f.place || bd?.place || "", timezone: f.timezone || bd?.timezone || "" }));
  });

  const birth = { date: form.date, time: `${form.hour}:${form.minute}`, place: form.place, timezone: form.timezone };

  const compute = async (quiet) => {
    setBusy(true);
    try {
      const r = await api.post("/career-remedy/compute", birth);
      setData(r.data);
      if (!quiet) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute your chart.");
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.date || !form.hour || !form.minute || !form.place) return toast.error("Fill in date, time and place of birth.");
    if (!form.timezone) return toast.error("Pick the birth place from the suggestions list.");
    compute(false);
  };

  useEffect(() => { setData(null); }, [form.date, form.hour, form.minute, form.place]);

  return (
    <PageShell>
      <SEO title="Career Remedy Nakshatra Secret | AstroKuberChat" description="Find your 10th lord and its nakshatra, and unlock a secret nakshatra remedy, one of the most powerful ways to activate your career." />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="career-remedy-page">
        <div className="label-eyebrow text-muted flex items-center gap-2"><Briefcase size={14} weight="duotone" /> Premium · 10th lord nakshatra remedy</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">Career Remedy <span className="text-gold">Nakshatra Secret</span></h1>
        <p className="text-sm text-muted mt-3 max-w-3xl">Your 10th house rules your career. Enter your birth details to find your 10th lord and its nakshatra, and unlock a secret nakshatra remedy that is one of the most powerful ways to activate your career.</p>

        <form onSubmit={submit} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 mt-8 grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end" data-testid="cr-form">
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Date of birth<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="cr-date" /></label>
          <div className="text-xs font-semibold text-[#3F2D1B]/70">Time of birth (24-hr)
            <div className="flex gap-2">
              <select value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} className={inputCls} data-testid="cr-hour"><option value="">HH</option>{HOURS.map((h) => <option key={h}>{h}</option>)}</select>
              <select value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} className={inputCls} data-testid="cr-minute"><option value="">MM</option>{MINUTES.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Place of birth
            <div className="mt-1"><PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v, timezone: "" })} onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone }))} placeholder="Start typing your city…" testId="cr-place" /></div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="cr-submit">
            <Sparkle size={15} weight="fill" /> {busy ? "Reading…" : "Find my remedy"}
          </button>
        </form>

        {data && (
          <div ref={resultRef} className="mt-10 space-y-6 scroll-mt-24" data-testid="cr-results">
            <LordCard d={data} />
            {data.remedy ? (
              <>
                <RemedyCard r={data.remedy} />
                <PlanetPicker planets={data.remedy.planets} initial={data.tenth_lord} />
                <LordCareers c={data.remedy.lord_careers} lord={data.tenth_lord} sign={data.tenth_sign} />
                <NakshatraDetails n={data.remedy.nakshatra} lord={data.tenth_lord} />
              </>
            ) : (
              <div data-testid="cr-paywall">
                <PaywallGate
                  tool="career_remedy"
                  toolLabel="Career Remedy Nakshatra Secret"
                  birthDetails={birth}
                  variant="standalone"
                  onUnlocked={() => { toast.success("Unlocked! Revealing your career remedy…"); compute(true); }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PageShell>
  );
}
